// Horizontal full-screen slide navigation: nav links, arrows, keyboard,
// mouse wheel / trackpad and touch swipe all move between sections.
// The deck loops: past the last slide comes the first one again (and back),
// with a quick fade instead of sliding back across every slide.
const TRANSITION_MS = 800;
const WRAP_FADE_MS = 280;
const WHEEL_THRESHOLD = 40;
const SWIPE_THRESHOLD = 50;

export default function initSlider() {
  const track = document.getElementById("track");
  const slides = [...track.querySelectorAll(".slide")];
  const navLinks = document.querySelectorAll(".site-nav__link");
  const prevBtn = document.getElementById("prev");
  const nextBtn = document.getElementById("next");

  let index = 0;
  let locked = false;
  let wheelDelta = 0;
  let wheelTimer;

  function goTo(target, { updateHash = true } = {}) {
    const next = ((target % slides.length) + slides.length) % slides.length;
    if (next === index && track.dataset.ready) return;
    const wrapped = track.dataset.ready && Math.abs(next - index) === slides.length - 1
      && (target < 0 || target >= slides.length);

    index = next;
    track.dataset.ready = "1";
    const position = `translate3d(${-index * 100}vw, 0, 0)`;
    if (wrapped) {
      track.classList.add("is-wrapping");
      setTimeout(() => {
        track.classList.add("no-transition");
        track.style.transform = position;
        void track.offsetWidth; // apply the jump before fading back in
        track.classList.remove("no-transition", "is-wrapping");
      }, WRAP_FADE_MS);
    } else {
      track.style.transform = position;
    }

    slides.forEach((slide, i) => {
      const active = i === index;
      slide.classList.toggle("is-active", active);
      slide.setAttribute("aria-hidden", String(!active));
      slide.inert = !active;
    });
    navLinks.forEach((link) =>
      link.classList.toggle("is-active", indexOfHash(link.hash) === index)
    );

    document.body.dataset.slide = slides[index].id;
    document.dispatchEvent(
      new CustomEvent("slidechange", { detail: { index, count: slides.length } })
    );

    if (updateHash) history.replaceState(null, "", `#${slides[index].id}`);

    locked = true;
    setTimeout(() => (locked = false), TRANSITION_MS);
  }

  function indexOfHash(hash) {
    return slides.findIndex((s) => `#${s.id}` === hash);
  }

  // A slide whose content is taller than the screen scrolls vertically first;
  // only once it reaches its edge does the wheel move to the next slide.
  function canScrollInside(deltaY) {
    const el = slides[index];
    if (el.scrollHeight <= el.clientHeight + 1) return false;
    if (deltaY > 0) return el.scrollTop + el.clientHeight < el.scrollHeight - 1;
    return el.scrollTop > 0;
  }

  document.querySelectorAll("[data-goto]").forEach((el) =>
    el.addEventListener("click", (e) => {
      e.preventDefault();
      const target = indexOfHash(el.hash);
      if (target !== -1) goTo(target);
    })
  );

  prevBtn.addEventListener("click", () => goTo(index - 1));
  nextBtn.addEventListener("click", () => goTo(index + 1));

  window.addEventListener("keydown", (e) => {
    if (e.target.closest("input, textarea")) return;
    if (["ArrowRight", "PageDown"].includes(e.key)) goTo(index + 1);
    else if (["ArrowLeft", "PageUp"].includes(e.key)) goTo(index - 1);
    else if (e.key === "Home") goTo(0);
    else if (e.key === "End") goTo(slides.length - 1);
  });

  window.addEventListener(
    "wheel",
    (e) => {
      const delta = Math.abs(e.deltaX) > Math.abs(e.deltaY) ? e.deltaX : e.deltaY;
      if (Math.abs(e.deltaY) >= Math.abs(e.deltaX) && canScrollInside(e.deltaY)) return;

      e.preventDefault();
      if (locked) return;

      wheelDelta += delta;
      clearTimeout(wheelTimer);
      wheelTimer = setTimeout(() => (wheelDelta = 0), 150);

      if (Math.abs(wheelDelta) >= WHEEL_THRESHOLD) {
        goTo(index + Math.sign(wheelDelta));
        wheelDelta = 0;
      }
    },
    { passive: false }
  );

  let touchX = 0;
  let touchY = 0;
  window.addEventListener(
    "touchstart",
    (e) => {
      touchX = e.touches[0].clientX;
      touchY = e.touches[0].clientY;
    },
    { passive: true }
  );
  window.addEventListener(
    "touchend",
    (e) => {
      const dx = e.changedTouches[0].clientX - touchX;
      const dy = e.changedTouches[0].clientY - touchY;
      if (Math.abs(dx) > SWIPE_THRESHOLD && Math.abs(dx) > Math.abs(dy) * 1.2) {
        goTo(index + (dx < 0 ? 1 : -1));
      }
    },
    { passive: true }
  );

  // The browser scrolls the (overflow: hidden) deck to bring a #hash target into
  // view; positioning is done with transforms only, so undo that scroll.
  const deck = track.parentElement;
  deck.addEventListener("scroll", () => {
    deck.scrollLeft = 0;
    deck.scrollTop = 0;
  });

  // Support back/forward and manually typed #hash links.
  window.addEventListener("hashchange", () => {
    const i = indexOfHash(location.hash);
    if (i !== -1) goTo(i, { updateHash: false });
  });

  const initial = indexOfHash(location.hash);
  track.classList.add("no-transition");
  goTo(initial === -1 ? 0 : initial, { updateHash: false });
  requestAnimationFrame(() =>
    requestAnimationFrame(() => track.classList.remove("no-transition"))
  );
}
